'use client';

import React, { ComponentPropsWithoutRef, ReactNode, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { AnimationPlaybackControls, animate, motion, useMotionValue } from 'framer-motion';

import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useIsRtl } from '@/hooks/useIsRtl';
import { EventBlockNames } from '@/providers/analytics/types';

import styles from './styles.module.scss';

export type RowProps<T> = {
  items: T[];
  renderItem(item: T): ReactNode;
  /**
   * direction of carousel
   */
  dir?: 'left' | 'right';
};

const Row = <T,>({ items, renderItem, dir = 'right' }: RowProps<T>) => {
  const bp = useBreakpoint();
  const { isRtl } = useIsRtl();
  const containerRef = useRef<HTMLDivElement>(null);
  const control = useRef<AnimationPlaybackControls>(null!);
  const xTranslation = useMotionValue(0);
  const duration = items.length * 2;
  const duplicatedItems = [...items, ...items];

  useEffect(() => {
    if (!containerRef.current || items.length === 0) return;

    const width = containerRef.current.scrollWidth;
    const gap = bp === 'desktop' ? 12 : 8;
    const scrollDistance = width / 2 + gap;
    const targetPosition = isRtl ? scrollDistance : -scrollDistance;
    const [startPosition, endPosition] = dir === 'right' ? [targetPosition, 0] : [0, targetPosition];

    control.current = animate(xTranslation, [startPosition, endPosition], {
      ease: 'linear',
      duration: duration,
      repeat: Infinity,
      repeatType: 'loop',
      repeatDelay: 0,
    });

    return () => {
      control?.current?.stop();
    };
  }, [items, xTranslation, bp, duration, isRtl, dir]);

  return (
    <div className={styles.carouselRow}>
      <motion.div
        className={styles.list}
        ref={containerRef}
        style={{ x: xTranslation }}
        onHoverStart={() => (control.current.speed = 0.2)}
        onHoverEnd={() => (control.current.speed = 1)}
      >
        {duplicatedItems.map((slide, index) => (
          <div key={index} className={styles.item}>
            {renderItem(slide)}
          </div>
        ))}
      </motion.div>
    </div>
  );
};

export const AssetsCarousel = ({ children, className }: ComponentPropsWithoutRef<'div'>) => {
  return (
    <div
      className={classNames(styles.carouselsBlock, className)}
      data-event-block-name={EventBlockNames.scrolling_assets_line}
    >
      {children}
    </div>
  );
};

AssetsCarousel.Row = Row;
